import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { LayoutDashboard, Box, Network, Shield, LogOut, Activity, Truck, Radio, Lightbulb, ShieldCheck, ShoppingCart, Settings } from "lucide-react";
import { usePermissions } from "../hooks/usePermissions";
import { useAuth } from "../contexts/AuthContext";
import KittLogo from "./KittLogo";
import UserSettingsModal, { Avatar } from "./UserSettingsModal";

/**
 * Build Prompt #14 — sidebar nav is permission-gated. Each entry declares which permission(s)
 * unlock it. If the user lacks the permission, the entry is REMOVED from the DOM (hard mask),
 * not greyed out. Mission Partners with `view_products_limited` see Products only.
 */
const NAV_ITEMS = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", anyPerm: ["view_dashboard_oversight"] },
    { to: "/products", label: "Products", icon: Box, testid: "nav-products", anyPerm: ["view_products", "view_products_limited"] },
    { to: "/logistics", label: "Logistics", icon: Truck, testid: "nav-logistics", anyPerm: ["view_logistics"] },
    { to: "/orders", label: "Orders", icon: ShoppingCart, testid: "nav-orders", anyPerm: ["manage_orders", "view_orders"] },
    { to: "/field-intel", label: "Field Intel", icon: Radio, testid: "nav-field-intel", anyPerm: ["view_field_intel"] },
    { to: "/concept-intake", label: "Concept Intake", icon: Lightbulb, testid: "nav-concept-intake", anyPerm: ["view_concept_intake", "submit_idea"] },
    { to: "/control-modes", label: "Control Modes", icon: Network, testid: "nav-control-modes", anyPerm: ["manage_control_modes"] },
    { to: "/gate-templates", label: "Gate Templates", icon: Shield, testid: "nav-gate-templates", anyPerm: ["manage_templates"] },
    { to: "/admin", label: "Admin Console", icon: ShieldCheck, testid: "nav-admin", anyPerm: ["manage_users", "manage_invitations", "manage_roles_engine", "view_system_health"] },
];

export default function Layout({ children }) {
    const { logout } = useAuth();
    const perm = usePermissions();
    const { user, roleLabel, roleBadgeClass } = perm;
    const navigate = useNavigate();
    const [settingsOpen, setSettingsOpen] = useState(false);

    const handleLogout = async () => {
        try {
            await logout();
        } catch (err) {
            console.warn("[Layout] logout failed:", err?.message || err);
        }
        navigate("/login");
    };

    const visibleNav = NAV_ITEMS.filter((item) => item.anyPerm.some((p) => perm.can(p)));

    return (
        <div className="min-h-screen flex bg-jet text-snow">
            {/* Sidebar */}
            <aside className="w-64 border-r border-hairline flex flex-col bg-jet sticky top-0 h-screen">
                <div className="px-5 py-6 border-b border-hairline">
                    <div className="flex flex-col items-center text-center gap-2">
                        <KittLogo size={88} testId="sidebar-kitt-logo" />
                        <div className="overline text-[8px] tracking-[0.32em] text-snow-muted leading-tight">
                            Kitting · Inventory<br />Transfer · Terminal
                        </div>
                    </div>
                </div>

                <nav className="flex-1 p-3 space-y-1" data-testid="sidebar-nav">
                    <div className="overline px-3 py-3">Operations</div>
                    {visibleNav.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={item.testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2 border-l-2 transition-colors duration-150 ${
                                    isActive
                                        ? "border-signal text-signal bg-signal/5"
                                        : "border-transparent text-snow-dim hover:text-snow hover:border-hairline"
                                }`
                            }
                        >
                            <item.icon className="w-4 h-4" />
                            <span className="text-sm uppercase tracking-wider">{item.label}</span>
                        </NavLink>
                    ))}
                </nav>

                <div className="p-3 border-t border-hairline space-y-2">
                    <button
                        type="button"
                        data-testid="sidebar-user-trigger"
                        onClick={() => setSettingsOpen(true)}
                        className="w-full text-left px-3 py-2 border border-hairline hover:border-signal hover:bg-signal/5 transition-colors duration-150 group"
                        title="Open user settings"
                    >
                        <div className="flex items-center gap-3">
                            <Avatar user={user} size={36} testId="sidebar-user-avatar" />
                            <div className="min-w-0 flex-1">
                                <div className="overline flex items-center gap-1.5">
                                    Operator
                                    <Settings className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                                <div className="text-sm text-snow truncate mt-0.5" data-testid="sidebar-user-name">
                                    {user?.name || user?.email || "—"}
                                </div>
                                {user?.job_title && (
                                    <div className="text-[10px] text-snow-muted font-mono truncate" data-testid="sidebar-user-job-title">
                                        {user.job_title}
                                    </div>
                                )}
                                <div className="font-mono text-[10px] text-snow-muted truncate mt-0.5" data-testid="sidebar-user-email">
                                    {user?.email ?? "—"}
                                </div>
                            </div>
                        </div>
                        <div className="mt-2">
                            <span
                                data-testid="sidebar-role-badge"
                                className={`inline-flex items-center border px-2 py-0.5 text-[10px] uppercase tracking-[0.25em] font-mono ${roleBadgeClass}`}
                            >
                                [ {roleLabel} ]
                            </span>
                        </div>
                    </button>
                    <button
                        data-testid="logout-btn"
                        onClick={handleLogout}
                        className="w-full flex items-center justify-center gap-2 border border-hairline px-3 py-2 text-xs uppercase tracking-wider text-snow-dim hover:border-signal hover:text-signal transition-colors duration-150"
                    >
                        <LogOut className="w-3 h-3" />
                        Sign Out
                    </button>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 flex flex-col">
                {/* Top status bar */}
                <div className="border-b border-hairline px-8 py-3 flex items-center justify-between bg-jet">
                    <div className="flex items-center gap-3 text-xs font-mono">
                        <Activity className="w-3 h-3 text-signal animate-pulse-signal" />
                        <span className="overline">System</span>
                        <span className="text-signal">Online</span>
                        <span className="text-snow-muted">|</span>
                        <span className="overline">Cluster</span>
                        <span className="text-snow-dim">CRB-01</span>
                    </div>
                    <div className="text-xs font-mono text-snow-muted tabular-nums">
                        {new Date().toUTCString()}
                    </div>
                </div>

                <div className="flex-1 p-8">{children}</div>
            </main>

            <UserSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
        </div>
    );
}
